import type { ViewjobQueryResult } from './__generated__/viewjob.gqlgenerated';
import type { HiringEventData } from './hooks/useConditionalHiringEventsData';
import type { RNVJNavigationTrigger } from './types/RNVJNavigationTrigger';
import type { ApplicantInsightsProctorData } from './utils/applicantInsightsUtils';
import type { EmployerInsightsProctorData } from './utils/employerInsightsUtils';
import type { BrandedJobProctorData } from './utils/employerUtils';
import type { ViewJob } from '../../__generated__/types.gqlgenerated';
import type { NativeContextRNProps } from '../../shared/props/NativeContextRNProps';
import type { RestResponse } from '../native-module/types/ViewJobNativeModule';
import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';

import { AppVJLoadedContent } from './AppVJLoadedContent';
import { NavigationBar } from './components/core/JobComponent/NavigationBar/NavigationBar';
import { ConditionalSafeAreaWrapper } from './components/core/SafeAreaWrapper';
import ViewJobDataContext, {
    type JobClickTrackingSelectedJobAttributes,
} from './context/ViewJobDataContext';
import { getPlacementIdFromUrl } from './hooks/getJobDataInput';
import { useApplyLink } from './hooks/useApplyLink';
import { useBrandedJobProctorData } from './hooks/useBrandedJobProctorData';
import { useFindResumeEngagement } from './hooks/useFindResumeEngagement';
import { useIsJsProVjProInsightsTogActive } from './hooks/useIsJsProVjProInsightsTogActive';
import { useJobDataResult } from './hooks/useJobDataResult';
import { useIsJsProMobVjEmpInsightsTstActive } from './hooks/useJsProMobVjEmpInsightsTst';
import { useMosaicProviderJsPremiumApplicantInsightsTogActive } from './hooks/useMosaicProviderJsPremiumApplicantInsightsTogActive';
import {
    useHasCtaAccess,
    useMosaicProviderProBannerVjTogPayload,
    useMosaicProviderProBannerVjTogVariant,
} from './hooks/useMosaicProviderProBannerVjTog';
import { useRestApiComparison } from './hooks/useRestApiComparison';
import { useScreenshotShare } from './hooks/useScreenshotShare';
import { useViewJobInitialData } from './hooks/useViewJobInitialData';
import { getPlatformOptions } from './PlatformOptions';
import { getDomain } from './util/getDomain';
import getJobKeyFromUrl from './util/getJobKeyFromUrl';
import { useGetApolloDefaultOptions } from './util/useGetApolloDefaultOptions';
import { fromParamUtils } from './utils/fromParamUtils';
import { type LoadingStatus, ViewJobLoadingState } from './ViewJobLoadingState';
import { composeUrlWithParams, createViewjobOneGraphModels } from './viewjobOneGraphModel';
import { jobCardDataLocalTypePolicies } from '../../jobcard/typePolicies';
import { OneGraphApolloClientProvider } from '../../shared/apollo';
import apolloErrorLink from '../../shared/apollo/apolloErrorLink';
import { useReportError } from '../../shared/components/ReportErrorProvider';
import { useI18n, useLocale } from '../../shared/I18n';
import { useLogFirstUserInteractionWithHandlers } from '../../shared/logging/interaction-tracker';
import { LoggerContext, LOGGER } from '../../shared/logging/logger';
import fetchNativeOneGraphOperation from '../../shared/native-modules/core/fetchNativeOneGraphQuery';
import { useTareData } from '../../shared/native-modules/tare/TareDataContext';
import { usePerformanceMeasurement } from '../../shared/util/performance';
import { useIsFullScreen } from '../../shared/util/ScreenType';
import { onDeepLinkLoadCompleted } from '../native-module/onDeepLinkLoadCompleted';
import { NotFoundErrorPage } from "./components/core/NotFoundErrorPage/NotFoundErrorPage";

import React, { useEffect, useMemo } from 'react';
import { StyleSheet } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

export interface AppContentProps {
    readonly url: string | null;
    readonly loggingUrl: string | null;
    readonly prforceGroups: string | null;
    readonly nativeContext?: NativeContextRNProps;
    readonly trigger?: RNVJNavigationTrigger;
    readonly source?: string;
    readonly reload?: () => void;
    readonly preloadedVJData?: model.viewjob.ViewJobClientSideModel & {
        jobClickTrackingAttributes?: JobClickTrackingSelectedJobAttributes;
    };
    readonly viewJobSource: 'ViewJob' | 'ViewJobFeed';
    getVJData: (url: string) => Promise<RestResponse<model.viewjob.ViewJobClientSideModel>>;
    /** SSR-supplied Apollo client. When set, the providers reuse it instead of creating one. */
    readonly apolloClient?: ApolloClient<NormalizedCacheObject>;
}

export function AppContentGraphQL({
    url,
    nativeContext,
    getVJData,
    prforceGroups,
    trigger,
    source,
    viewJobSource,
    apolloClient,
    preloadedVJData,
}: AppContentProps) {
    const locale = useLocale();
    const errorLink = useMemo(
        () => apolloErrorLink({ provider: 'ViewJob', nativeContext }),
        [nativeContext],
    );

    const defaultOptions = useGetApolloDefaultOptions({
        ctk: nativeContext?.ctk,
        locale,
    });

    const platformOptions = useMemo(() => getPlatformOptions(), []);

    const placementId = useMemo(() => getPlacementIdFromUrl(url) ?? undefined, [url]);

    return (
        <OneGraphApolloClientProvider
            fetch={fetchNativeOneGraphOperation}
            defaultOptions={defaultOptions}
            platformOptions={platformOptions}
            prforceGroups={prforceGroups ?? undefined}
            errorLink={errorLink}
            placementId={placementId}
            typePolicies={jobCardDataLocalTypePolicies}
            externalClient={apolloClient}
        >
            <LoggerContext.Provider value={LOGGER}>
                <ContentWithInitialData
                    url={url}
                    nativeContext={nativeContext}
                    getVJData={getVJData}
                    prforceGroups={prforceGroups}
                    trigger={trigger}
                    source={source}
                    viewJobSource={viewJobSource}
                    apolloClient={apolloClient}
                    jobClickTrackingAttributes={preloadedVJData?.jobClickTrackingAttributes}
                />
            </LoggerContext.Provider>
        </OneGraphApolloClientProvider>
    );
}

interface ContentWithInitialDataProps {
    url: string | null;
    nativeContext: NativeContextRNProps | undefined;
    getVJData: (url: string) => Promise<RestResponse<model.viewjob.ViewJobClientSideModel>>;
    prforceGroups: string | null;
    trigger: RNVJNavigationTrigger | undefined;
    source: string | undefined;
    viewJobSource: 'ViewJob' | 'ViewJobFeed';
    apolloClient?: ApolloClient<NormalizedCacheObject>;
    jobClickTrackingAttributes?: JobClickTrackingSelectedJobAttributes;
}

function ContentWithInitialData(props: ContentWithInitialDataProps) {
    const {
        url,
        nativeContext,
        getVJData,
        prforceGroups,
        trigger,
        source,
        viewJobSource,
        apolloClient,
        jobClickTrackingAttributes,
    } = props;
    const locale = useLocale();
    const jobKey = getJobKeyFromUrl(url);
    const errorLink = useMemo(
        () => apolloErrorLink({ provider: 'ViewJob', nativeContext }),
        [nativeContext],
    );

    const defaultOptions = useGetApolloDefaultOptions({
        ctk: nativeContext?.ctk,
        locale,
    });

    const platformOptions = useMemo(() => getPlatformOptions(), []);

    const placementId = useMemo(() => getPlacementIdFromUrl(url) ?? undefined, [url]);

    // These two queries run IN PARALLEL
    const {
        loading: viewJobLoading,
        data,
        error,
        queryStatus: viewJobQueryStatus,
        refetch: viewJobRetryRequest,
        fetchStartTimeMs,
        fetchEndTimeMs,
    } = useViewJobInitialData(url, locale);

    const {
        jobDataResult,
        jobDataResultLoading,
        queryStatus: jobDataResultQueryStatus,
        refetchJobDataResult,
    } = useJobDataResult({ jobKey, url });

    const loading = viewJobLoading || jobDataResultLoading;

    // Combine query statuses: viewJob error is critical, jobDataResult error is non-blocking
    const queryStatus = (() => {
        if (viewJobQueryStatus === 'active' || jobDataResultQueryStatus === 'active') {
            return 'active';
        }
        if (viewJobQueryStatus === 'error') {
            return 'error';
        }
        if (viewJobQueryStatus === 'success') {
            return 'success';
        }
        return 'idle';
    })();

    const retryRequest = () => {
        viewJobRetryRequest();
        void refetchJobDataResult();
    };

    const { logHttpError } = useReportError();

    useEffect(() => {
        if (!loading) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises -- FIXME
            onDeepLinkLoadCompleted();

            if (error) {
                logHttpError(url ?? '', error.message, 0, nativeContext);
            }
        }
    }, [loading, error, logHttpError, nativeContext, url]);

    return (
        // For the secondary queries. All the subsequent queries would have access to root tracking key
        <OneGraphApolloClientProvider
            fetch={fetchNativeOneGraphOperation}
            defaultOptions={defaultOptions}
            platformOptions={platformOptions}
            rootTrackingKey={data?.viewJobData?.viewjob?.details?.mobvjtk}
            prforceGroups={prforceGroups ?? undefined}
            errorLink={errorLink}
            placementId={placementId}
            typePolicies={jobCardDataLocalTypePolicies}
            externalClient={apolloClient}
        >
            <ContentWithApplyLink
                data={data}
                jobKey={jobKey}
                url={url}
                nativeContext={nativeContext}
                getVJData={getVJData}
                prforceGroups={prforceGroups}
                trigger={trigger}
                source={source}
                retryRequest={retryRequest}
                fetchStartTimeMs={fetchStartTimeMs}
                fetchEndTimeMs={fetchEndTimeMs}
                queryStatus={queryStatus}
                jobDataResult={jobDataResult}
                viewJobSource={viewJobSource}
                jobClickTrackingAttributes={jobClickTrackingAttributes}
            />
        </OneGraphApolloClientProvider>
    );
}

interface ContentWithApplyLinkProps {
    url: string | null;
    jobKey: string | null | undefined;
    data: {
        viewJobData: ViewjobQueryResult['data'];
        hiringEventsData?: HiringEventData;
    };
    nativeContext: NativeContextRNProps | undefined;
    getVJData: (url: string) => Promise<RestResponse<model.viewjob.ViewJobClientSideModel>>;
    prforceGroups: string | null;
    trigger: RNVJNavigationTrigger | undefined;
    source: string | undefined;
    retryRequest: () => void;
    fetchStartTimeMs: number | null;
    fetchEndTimeMs: number | null;
    queryStatus: LoadingStatus;
    jobDataResult: ReturnType<typeof useJobDataResult>['jobDataResult'];
    viewJobSource: 'ViewJob' | 'ViewJobFeed';
    jobClickTrackingAttributes?: JobClickTrackingSelectedJobAttributes;
}

function ContentWithApplyLink(props: ContentWithApplyLinkProps) {
    const withTopSafeArea = useIsFullScreen();
    const {
        data: { hiringEventsData, viewJobData },
        jobKey,
        url,
        nativeContext,
        getVJData,
        prforceGroups,
        trigger,
        source,
        fetchStartTimeMs,
        fetchEndTimeMs,
        queryStatus,
        retryRequest,
        jobDataResult,
    } = props;
    const tareData = useTareData();
    const locale = useLocale();
    const i18n = useI18n();

    // Performance measurement for TTFF, TTI, and FID
    const performanceResult = usePerformanceMeasurement({
        provider: 'viewjob',
        componentName: 'ContentWithApplyLink',
        grps: nativeContext?.proctorGroups,
    });
    const { markInteractive, panResponder: originalPanResponder } = performanceResult;

    // Use custom hook for resume engagement query
    const resumeEngagementContext = useFindResumeEngagement();

    // Fetch applyLink asynchronously (depends on viewjob data but doesn't block UI)
    const { applyLinkUrl: rawApplyLinkUrl, applyLinkLoading } = useApplyLink({
        jobKey,
        viewJobData,
        viewJobQueryCompleted: queryStatus === 'success',
        url,
    });

    const composedApplyLinkUrl = useMemo(() => {
        if (!rawApplyLinkUrl) return null;
        const applyMethod = viewJobData?.viewjob?.details?.applyMethod;
        const additionalUrlParameters =
            applyMethod?.__typename === 'ViewJobIndeedApply'
                ? applyMethod.additionalUrlParameters
                : null;
        return composeUrlWithParams(rawApplyLinkUrl, additionalUrlParameters);
    }, [rawApplyLinkUrl, viewJobData?.viewjob?.details?.applyMethod]);

    const hasCtaAccess = useHasCtaAccess();
    const isJsPremiumApplicantInsightsActive =
        useMosaicProviderJsPremiumApplicantInsightsTogActive();
    const isJsProVjProInsightsTogActive = useIsJsProVjProInsightsTogActive();
    const proctorPayload = useMosaicProviderProBannerVjTogPayload();
    const variant = useMosaicProviderProBannerVjTogVariant();
    const isJsProMobVjEmpInsightsTstActive = useIsJsProMobVjEmpInsightsTstActive();

    const applicantInsightsProctorData: ApplicantInsightsProctorData = useMemo(
        () => ({
            hasCtaAccess,
            isJsPremiumApplicantInsightsActive,
            isJsProVjProInsightsTogActive,
            proctorPayload,
            variant,
        }),
        [
            hasCtaAccess,
            isJsPremiumApplicantInsightsActive,
            isJsProVjProInsightsTogActive,
            proctorPayload,
            variant,
        ],
    );

    const employerInsightsProctorData: EmployerInsightsProctorData = useMemo(
        () => ({
            isJsProVjProInsightsTogActive,
            isJsProMobVjEmpInsightsTstActive,
        }),
        [isJsProVjProInsightsTogActive, isJsProMobVjEmpInsightsTstActive],
    );

    const {
        isBrandedJobMigrationTestActive,
        isBrandedJobMigrationShadow,
    } = useBrandedJobProctorData();
    const brandedJobProctorData: BrandedJobProctorData = useMemo(
        () => ({
            isBrandedJobMigrationTestActive,
            isBrandedJobMigrationShadow,
        }),
        [isBrandedJobMigrationTestActive, isBrandedJobMigrationShadow],
    );

    const viewJobModel = useMemo(() => {
        if (!viewJobData?.viewjob?.job || queryStatus !== 'success') {
            return null;
        }
        return createViewjobOneGraphModels(
            url ?? '',
            viewJobData.viewjob as ViewJob,
            viewJobData.jobSeekerProfile ?? null,
            i18n,
            tareData,
            jobDataResult,
            applicantInsightsProctorData,
            employerInsightsProctorData,
            brandedJobProctorData,
        );
    }, [
        viewJobData?.viewjob,
        viewJobData?.jobSeekerProfile,
        queryStatus,
        url,
        i18n,
        tareData,
        jobDataResult,
        applicantInsightsProctorData,
        employerInsightsProctorData,
        brandedJobProctorData,
    ]);

    // Get mobtk and from for logging
    const mobtk = viewJobModel?.mobvjtk;
    const from = fromParamUtils(url);

    // Handle screenshot sharing with job details
    useScreenshotShare(
        viewJobModel
            ? {
                  shareUrl: viewJobModel.shareModel?.url ?? '',
                  jobTitle: viewJobModel.job?.jobTitle ?? '',
              }
            : null,
    );

    // Create wrapped panResponder that logs first interaction
    const panHandlers = useLogFirstUserInteractionWithHandlers(
        mobtk,
        from,
        originalPanResponder.panHandlers,
    );

    // Mark as interactive when ViewJob data is loaded and ready
    useEffect(() => {
        if (viewJobModel) {
            markInteractive();
        }
    }, [viewJobModel, markInteractive]);

    // Use the REST API comparison hook for debugging purposes
    useRestApiComparison({
        url,
        locale,
        prforceGroups,
        getVJData,
        nativeContext,
        trigger,
        source,
        graphqlModel: viewJobModel,
        graphqlData: viewJobData,
    });

    return (
        <ViewJobDataContext.Provider
            value={{
                domain: getDomain(url),
                viewjobSourceUrl: url || '',
                viewjobOneGraphModel: viewJobModel ?? undefined,
                hiringEventsData: hiringEventsData,
                proctorGroups: nativeContext?.proctorGroups,
                resumeEngagementContext,
                from: fromParamUtils(url),
                applyLinkUrl: composedApplyLinkUrl,
                applyLinkLoading,
                jobClickTrackingAttributes: props.jobClickTrackingAttributes,
            }}
        >
            <ConditionalSafeAreaWrapper withTopSafeArea={withTopSafeArea} panHandlers={panHandlers}>
                <NavigationBar
                    url={url}
                    showSaveJobButton={queryStatus === 'success'}
                    showShareButton={queryStatus === 'success'}
                    isLoggedIn={
                        queryStatus === 'success' && (viewJobModel?.jobSeeker?.loggedIn ?? false)
                    }
                    reload={retryRequest}
                    jobTitle={viewJobModel?.job?.jobTitle}
                    renderedOn={props.viewJobSource}
                />
                <SafeAreaView style={styles.container} edges={['left', 'bottom', 'right']}>
                    <ViewJobLoadingState status={queryStatus} onRetry={retryRequest} url={url}>
                        {viewJobModel ? (
                            <AppVJLoadedContent
                                reload={retryRequest}
                                navigationStartTimeMs={nativeContext?.navigationStartTimeMs ?? 0}
                                fetchStartTimeMs={fetchStartTimeMs}
                                fetchEndTimeMs={fetchEndTimeMs}
                            />
                        ) : <NotFoundErrorPage url={url}/>}
                    </ViewJobLoadingState>
                </SafeAreaView>
            </ConditionalSafeAreaWrapper>
        </ViewJobDataContext.Provider>
    );
}

const styles = StyleSheet.create({
    outerContainer: {
        flex: 1,
    },
    container: {
        flex: 1,
        justifyContent: 'center',
    },
});
